import '../styles/LandingInstrumentHero.css';
import React, { useState, useEffect, useRef } from 'react';

const GAUGE_STAGES = ['Picked up', 'In transit', 'Customs', 'Out for delivery', 'Delivered'];
const GAUGE_STAGE = 4; // Out for delivery
const TARGET_ETA = 3; // days

const ARC_RADIUS = 32;
const ARC_CIRCUMFERENCE = 2 * Math.PI * ARC_RADIUS;
const GAUGE_ARC_FRACTION = 0.75; // 270° sweep, gap at the bottom
const NEEDLE_START = -90 - (360 * GAUGE_ARC_FRACTION) / 2;
const NEEDLE_STEP = (360 * GAUGE_ARC_FRACTION) / (GAUGE_STAGES.length - 1);
const NEEDLE_ANGLE = NEEDLE_START + NEEDLE_STEP * (GAUGE_STAGE - 1);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function LandingInstrumentHero() {
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [etaValue, setEtaValue] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);
  const [imgReady, setImgReady] = useState(false);

  // one-shot reveal + needle sweep trigger
  useEffect(() => {
    const node = rootRef.current;
    if (!node) {
      setInView(true);
      return undefined;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // one-shot tabular ETA count-up, then hold still
  useEffect(() => {
    if (!inView) return undefined;
    if (prefersReducedMotion()) {
      setEtaValue(TARGET_ETA);
      return undefined;
    }
    let rafId = 0;
    let start = 0;
    const duration = 900;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setEtaValue(Math.round(TARGET_ETA * p));
      if (p < 1) rafId = window.requestAnimationFrame(step);
    };
    rafId = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(rafId);
  }, [inView]);

  const rootClass = 'lih-root' + (inView ? ' lih-in' : '');
  const routeClass = 'lih-route' + (inView ? ' lih-in' : '');
  const dashOffset = ARC_CIRCUMFERENCE * (1 - GAUGE_ARC_FRACTION);

  return (
    <section className={rootClass} ref={rootRef} aria-label="Fast Forward Deliver Services">
      <div className="lih-hero">
        <div className="lih-type-block">
          <div className="lih-type-inner">
            <p className="lih-kicker lih-reveal" data-step="1">
              <span className="lih-kicker-rule" aria-hidden="true" />
              Consignment shipment · tracked handover to delivery
            </p>

            <h1 className="lih-wordmark lih-reveal" data-step="2">
              <span className="lih-wordmark-line">FAST</span>
              <span className="lih-wordmark-line">FORWARD</span>
            </h1>
            <span className="lih-sublabel lih-reveal" data-step="3">
              Deliver Services
            </span>

            <div className="lih-baseline-rule" aria-hidden="true" />

            <div className="lih-actions lih-reveal" data-step="4">
              <a className="lih-cta" href="/track-shipment">
                Track a shipment
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path
                    d="M2 8h11M9 4l4 4-4 4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
              <a className="lih-secondary" href="/login">
                Returning participant sign in
              </a>
            </div>
          </div>
        </div>

        <div className="lih-image-block">
          <div className="lih-turntable">
            {!imgFailed ? (
              <img
                className="lih-media"
                src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1800&q=70"
                alt="Macro detail of a machined titanium case edge lit from the top left"
                onError={() => setImgFailed(true)}
                onLoad={() => setImgReady(true)}
                style={imgReady ? undefined : { opacity: 0 }}
              />
            ) : null}
          </div>

          {imgFailed ? (
            <div className="lih-image-fallback">
              Instrument case edge — image unavailable
            </div>
          ) : null}

          <div className="lih-media-duotone" aria-hidden="true" />
          <div className="lih-amber-edge" aria-hidden="true" />

          <svg
            className={routeClass}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M14 20 C 34 6, 52 34, 66 28 C 80 22, 86 54, 89 76" />
            <g className="lih-pin-origin">
              <circle className="lih-pin-ring" cx="14" cy="20" r="2.4" />
              <circle className="lih-pin-dot" cx="14" cy="20" r="1.1" />
            </g>
            <g className="lih-pin-dest">
              <circle className="lih-pin-ring" cx="89" cy="76" r="2.4" />
              <circle className="lih-pin-dot" cx="89" cy="76" r="1.1" />
            </g>
          </svg>

          <div className="lih-gauge" role="img" aria-label="Current shipment stage: Out for delivery, estimated 3 days">
            <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
              <circle className="lih-gauge-bezel" cx="50" cy="50" r="40" />

              <g transform="rotate(135 50 50)">
                <circle
                  className="lih-gauge-track"
                  cx="50"
                  cy="50"
                  r={ARC_RADIUS}
                  strokeDasharray={`${ARC_CIRCUMFERENCE * GAUGE_ARC_FRACTION} ${ARC_CIRCUMFERENCE}`}
                />
                <circle
                  className="lih-gauge-arc"
                  cx="50"
                  cy="50"
                  r={ARC_RADIUS}
                  strokeDasharray={`${ARC_CIRCUMFERENCE * GAUGE_ARC_FRACTION} ${ARC_CIRCUMFERENCE}`}
                  style={{ strokeDashoffset: inView || prefersReducedMotion() ? dashOffset : ARC_CIRCUMFERENCE }}
                />
              </g>

              <g transform="rotate(135 50 50)">
                {[0, 1, 2, 3, 4].map((tick) => {
                  const a = (tick * Math.PI) / 2;
                  const x1 = 50 + 26 * Math.cos(a);
                  const y1 = 50 + 26 * Math.sin(a);
                  const x2 = 50 + 22 * Math.cos(a);
                  const y2 = 50 + 22 * Math.sin(a);
                  return (
                    <line
                      key={`tick-${tick}`}
                      className="lih-gauge-tick"
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                    />
                  );
                })}
              </g>

              <line
                className="lih-gauge-needle"
                x1="50"
                y1="50"
                x2="50"
                y2="20"
                style={{
                  transform: `rotate(${inView || prefersReducedMotion() ? NEEDLE_ANGLE : NEEDLE_START}deg)`,
                }}
              />
              <circle className="lih-gauge-hub-ring" cx="50" cy="50" r="5" />
              <circle className="lih-gauge-hub" cx="50" cy="50" r="2.6" />
            </svg>

            <div className="lih-gauge-readout">
              <div className="lih-gauge-label">
                <span className="lih-gauge-eyebrow">Stage {GAUGE_STAGE} / 5</span>
                {GAUGE_STAGES[GAUGE_STAGE - 1]}
              </div>
              <div className="lih-eta">
                <span className="lih-eta-value">{etaValue}</span>
                <span className="lih-eta-unit">
                  {etaValue === 1 ? 'day' : 'days'} ETA
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LandingInstrumentHero;